import { useCallback, useEffect, useRef, useState } from 'react';

type UseTransientLoadingStateArgs = {
  durationMs?: number;
};

type UseTransientLoadingStateResult = {
  isLoading: boolean;
  triggerLoading: () => void;
};

export const useTransientLoadingState = ({
  durationMs = 150,
}: UseTransientLoadingStateArgs = {}): UseTransientLoadingStateResult => {
  const [isLoading, setIsLoading] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  const clearTimer = useCallback((): void => {
    if (timeoutRef.current === null || typeof window === 'undefined') {
      return;
    }

    window.clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  }, []);

  const triggerLoading = useCallback((): void => {
    setIsLoading(true);
    clearTimer();

    if (typeof window === 'undefined') {
      setIsLoading(false);
      return;
    }

    timeoutRef.current = window.setTimeout(() => {
      setIsLoading(false);
      timeoutRef.current = null;
    }, durationMs);
  }, [clearTimer, durationMs]);

  useEffect(() => clearTimer, [clearTimer]);

  return { isLoading, triggerLoading };
};
